import { Injectable } from '@angular/core';
import {
    FormGroupType,
    NestedFormGroupType,
} from '../../../../../../core/models/form-group-type';
import { EmissionDesignator } from '../../models/emission-designator';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActionCode } from '../../../../../../core/models/resources/action-code';
import { DropdownOption } from '../../../../../../core/models/dropdown-option';
import { FrequencyFormModel } from '../../models/frequency-form-model';
import { EmissionDesignatorSortModel } from '../../../../shared/services/emission-designator-sorting/models/emission-designator-sort-model';
import { EmissionDesignatorSortingService } from '../../../../shared/services/emission-designator-sorting/emission-designator-sorting.service';
import { EmissionDesignatorConstants } from '../../constants/emission-designator-constants';

@Injectable({
    providedIn: 'root',
})
export class EmissionDesignatorFormService {
    constructor(
        private readonly emissionDesignatorSortingService: EmissionDesignatorSortingService,
    ) {}

    getFormDefinition(): FormGroup<FormGroupType<EmissionDesignator>> {
        return new FormGroup<FormGroupType<EmissionDesignator>>({
            id: new FormControl<number | null>(null),
            code: new FormControl<string | null>(null, [Validators.required]),
            actionCode: new FormControl<ActionCode | null>(ActionCode.Add),
            manualEmissionCode: new FormControl<boolean | null>(null),
        });
    }

    /**
     * Sorts the emission designators by bandwidth in ascending order
     * @param list
     * @returns
     */
    getSortedEmissionDesignatorValues(
        list: EmissionDesignator[],
    ): EmissionDesignator[] {
        const sortedEmissionDesignatorsCodesOnly: EmissionDesignatorSortModel[] =
            this.emissionDesignatorSortingService.sortByBandwidthDescending(
                list
                    .filter((x) => x.code !== null)
                    .map((emissionDesignator) => {
                        const emissionDesignatorFormModel: EmissionDesignatorSortModel =
                            {
                                emissionDesignatorCode:
                                    emissionDesignator.code!,
                            };

                        return emissionDesignatorFormModel;
                    }),
            );

        const sortedEmissionDesignators: EmissionDesignator[] = [];
        sortedEmissionDesignatorsCodesOnly.forEach(
            (sortedEmissionDesignator) => {
                const emissionDesignator = list.find(
                    (x) =>
                        x.code ===
                        sortedEmissionDesignator.emissionDesignatorCode,
                );

                if (emissionDesignator) {
                    sortedEmissionDesignators.push(emissionDesignator);
                }
            },
        );

        return sortedEmissionDesignators;
    }

    /**
     * Checks if the manual emission designator option should be shown (custom input by the user) will be shown as `CODE (Manual)`
     * @returns true if the manual emission designator option should be shown
     */
    shouldShowManualEmissionDesignatorOption(
        isManualEmissionOption: boolean,
        addEmissionDesignatorFormControl: FormControl<string>,
    ): boolean {
        return (
            isManualEmissionOption === true &&
            addEmissionDesignatorFormControl.value.length >=
                EmissionDesignatorConstants.MINIMUM_EMISSION_DESIGNATOR_LENGTH &&
            addEmissionDesignatorFormControl.value.length <=
                EmissionDesignatorConstants.MAXIMUM_EMISSION_DESIGNATOR_LENGTH
        );
    }

    addEmissionDesignator(
        emissionDesignator: EmissionDesignator,
        formArray: FormArray<FormGroup<FormGroupType<EmissionDesignator>>>,
    ): void {
        const code =
            emissionDesignator.code?.toUpperCase() ?? emissionDesignator.code;
        const addedEmissionDesignatorForm = this.getFormDefinition();

        addedEmissionDesignatorForm.patchValue({ ...emissionDesignator, code });

        const isEmissionDesignatorAlreadyAdded = formArray.controls.some(
            (control) => control.value.code === code,
        );

        if (isEmissionDesignatorAlreadyAdded) {
            return;
        }

        formArray.push(addedEmissionDesignatorForm);
    }

    getAddEmissionDesignatorDropdownOptions(
        frequencies: FormGroup<NestedFormGroupType<FrequencyFormModel>>[],
        initialList: DropdownOption[],
        formArray: FormArray<FormGroup<FormGroupType<EmissionDesignator>>>,
        isSingleFrequencyAction: boolean,
    ): DropdownOption[] {
        let list = initialList;

        formArray.controls.forEach((control) => {
            list = this.getEmissionDesignatorsListWithoutCode(
                list,
                control.value.code!,
            );
        });

        if (isSingleFrequencyAction !== true) {
            const common =
                this.getCommonEmissionDesignatorsFromAllFrequencies(
                    frequencies,
                );

            common.forEach((emissionDesignator: EmissionDesignator) => {
                list = this.getEmissionDesignatorsListWithoutCode(
                    list,
                    emissionDesignator.code!,
                );
            });
        }

        return list;
    }

    getFilteredAddEmissionDesignatorAutoCompleteOptions(
        value: string,
        availableToAddEmissionDesignatorOptions: DropdownOption[],
    ): DropdownOption[] {
        if (value === '') {
            return availableToAddEmissionDesignatorOptions;
        }

        const filterValue = value!.toUpperCase();

        return availableToAddEmissionDesignatorOptions.filter((option) =>
            option.label.toUpperCase().startsWith(filterValue),
        );
    }

    getCommonEmissionDesignatorsFromAllFrequencies(
        frequencies: FormGroup<NestedFormGroupType<FrequencyFormModel>>[],
    ): EmissionDesignator[] {
        const emissionDesignatorsFromAllFrequenciesSelected =
            this.getEmissionDesignatorsFromAllFrequencies(frequencies);

        return emissionDesignatorsFromAllFrequenciesSelected.reduce(
            (accumulator, currentValue) => {
                return accumulator.filter((emissionDesignator) =>
                    currentValue.some(
                        (value) =>
                            value.code === emissionDesignator.code &&
                            value.actionCode === emissionDesignator.actionCode,
                    ),
                );
            },
        );
    }

    getEmissionDesignatorsFromAllFrequencies(
        frequencies: FormGroup<NestedFormGroupType<FrequencyFormModel>>[],
    ): EmissionDesignator[][] {
        const emissionDesignatorsFromAllFrequenciesSelected: EmissionDesignator[][] =
            [];

        frequencies.forEach((control, index) => {
            const emissionDesignators = control.controls
                .emissionDesignators as FormArray<
                FormGroup<FormGroupType<EmissionDesignator>>
            >;

            emissionDesignatorsFromAllFrequenciesSelected.push([]);

            emissionDesignators.controls.forEach((emissionDesignator) => {
                emissionDesignatorsFromAllFrequenciesSelected[index].push(
                    emissionDesignator.value as EmissionDesignator,
                );
            });
        });

        return emissionDesignatorsFromAllFrequenciesSelected;
    }

    getEmissionDesignatorsListWithoutCode(
        list: DropdownOption[],
        code: string,
    ): DropdownOption[] {
        return list.filter((option) => option.value !== code);
    }
}
